import * as React from 'react';
import { cn } from './utils';

const baseClasses =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-cursor-pointer active:tw-scale-[0.97] active:tw-brightness-95 disabled:tw-pointer-events-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed';

const variantClasses = {
  default: 'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90',
  destructive: 'tw-bg-destructive tw-text-white hover:tw-bg-destructive/90',
  outline: 'tw-border tw-bg-background tw-text-foreground hover:tw-bg-accent hover:tw-text-accent-foreground',
  secondary: 'tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80',
  ghost: 'hover:tw-bg-accent hover:tw-text-accent-foreground',
  link: 'tw-text-primary tw-underline-offset-4 hover:tw-underline',
};

const sizeClasses = {
  default: 'tw-h-9 tw-px-4 tw-py-2',
  sm: 'tw-h-8 tw-px-3',
  lg: 'tw-h-10 tw-px-6',
  icon: 'tw-size-9',
};

type ButtonVariant = keyof typeof variantClasses;
type ButtonSize = keyof typeof sizeClasses;

const buttonVariants = ({
  variant = 'default',
  size = 'default',
  className = '',
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} = {}) => cn(baseClasses, variantClasses[variant], sizeClasses[size], className);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  children,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  variant?: keyof typeof variantClasses;
  size?: keyof typeof sizeClasses;
}) {
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ className?: string }>;
    return React.cloneElement(child, {
      ...props,
      className: cn(buttonVariants({ variant, size }), className, child.props.className),
    } as Record<string, unknown>);
  }

  return (
    <button data-slot="button" className={cn(buttonVariants({ variant, size }), className)} {...props}>
      {children}
    </button>
  );
}

export { Button, buttonVariants };
